import Axios from 'helpers/Interceptor';

export enum TaskType {
  CAMPAIGN = 'campaign',
  MARKETING_PLAN = 'marketing_plan',
  WEBSITE = 'website',
  ASSET_GENERATION = 'asset_generation',
  AUDIENCE_ANALYSIS = 'audience_analysis',
  CONTENT_ANALYSIS = 'content_analysis',
  PRESAVE = 'presave',
  ANALYTICS = 'analytics',
  GENERAL = 'general'
}

export enum TaskStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded'
}

export interface TaskUsage {
  used: number;
  remaining: number;
  monthlyLimit: number;
  extraTasksAvailable: number;
  resetDate: string;
  planType: string;
  taskPackInfo: {
    packSize: number;
    packPriceCents: number;
  };
}

export interface TaskHistoryItem {
  id: string;
  taskType: TaskType;
  status: TaskStatus;
  resourceId: string;
  metadata: any;
  createdAt: string;
  completedAt?: string;
  userName: string;
}

export interface TaskHistory {
  tasks: TaskHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface TaskStats {
  usage: TaskUsage;
  recentActivity: {
    total: number;
    completed: number;
    failed: number;
    pending: number;
  };
  taskPackInfo: {
    packSize: number;
    packPriceCents: number;
  };
}

export interface TaskAvailability {
  canConsume: boolean;
  tasksRemaining: number;
  needsUpgrade: boolean;
  message: string;
}

export interface TaskLimit {
  planType: string;
  monthlyTaskLimit: number;
  taskPackSize: number;
  taskPackPriceCents: number;
}

/** Get current task usage and limits for a brand */
export const getTaskUsage_api = async (brandId: number): Promise<TaskUsage> => {
  const response = await Axios.get<TaskUsage>(`/tasks/usage/${brandId}`);
  return response.data;
};

/** Get task history with optional filtering and pagination */
export const getTaskHistory_api = async (
  brandId: number,
  options: {
    limit?: number;
    offset?: number;
    taskType?: TaskType;
    status?: TaskStatus;
  } = {}
): Promise<TaskHistory> => {
  const params = new URLSearchParams();
  
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());
  if (options.taskType) params.append('taskType', options.taskType);
  if (options.status) params.append('status', options.status);
  
  const queryString = params.toString();
  const url = `/tasks/history/${brandId}${queryString ? `?${queryString}` : ''}`;
  
  const response = await Axios.get<TaskHistory>(url);
  return response.data;
};

/** Get comprehensive task statistics for dashboard display */
export const getTaskStats_api = async (brandId: number): Promise<TaskStats> => {
  const response = await Axios.get<TaskStats>(`/tasks/stats/${brandId}`);
  return response.data;
};

/** Check if a brand can consume a specific task type without actually consuming it */
export const checkTaskAvailability_api = async (
  brandId: number,
  taskType: TaskType
): Promise<TaskAvailability> => {
  const response = await Axios.post<TaskAvailability>('/tasks/check-availability', {
    brandId,
    taskType
  });
  return response.data;
};

/** Get all plan limits and pricing information (admin access required) */
export const getAllTaskLimits_api = async (): Promise<TaskLimit[]> => {
  const response = await Axios.get<TaskLimit[]>('/tasks/limits');
  return response.data;
};/** Unified task interface for both assistant and recommended tasks */

export interface AgentTaskOptions {
  id: string;
  type: TaskType;
  title: string;
  prompt?: string;
  description: string;
  campaignId?: string;
  chatId?: string;
  priority: 'high' | 'medium' | 'low';
  /** Optional fields for backward compatibility */
  estimatedTime?: string;
  relatedContent?: string | null;

  shouldLinkToPage?: string;
}
 